import React from 'react';

interface ChunkBoundaryProps {
  children: React.ReactNode;
  /** When provided, "Try again" calls this instead of reloading the page, then
   *  clears the error so children re-render in place. React.lazy() caches a
   *  rejected import forever on the lazy component reference itself, so a
   *  same-session retry only works if the callback constructs a brand new
   *  React.lazy(factory) (see MetricCard/Dashboard) — resetting state alone
   *  would just re-throw the same cached rejection. Omit for a full reload
   *  (e.g. App.tsx's admin-panel usage). */
  onRetry?: () => void;
  /** When true, a caught error that ISN'T a chunk-load failure is rethrown
   *  from render() so it propagates to the nearest ancestor boundary instead
   *  of being shown here as "couldn't load this section" — this boundary
   *  only owns chunk failures, not arbitrary render bugs in the lazy-loaded
   *  component. Defaults to false, which catches everything (the pre-Task-11
   *  behavior): App.tsx's admin-panel usage relies on that default because
   *  there is no boundary above App to catch a rethrown error — flipping this
   *  on there would turn a genuine AdminPanel bug into an uncaught white
   *  screen. Per-chart usage in MetricCard/Dashboard passes true explicitly. */
  rethrowNonChunkErrors?: boolean;
}

interface ChunkBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/** A chunk-load failure surfaces either as a `ChunkLoadError` (webpack's name
 *  for it) or as one of the browser/webpack-runtime messages below, depending
 *  on browser and failure mode (404 on the chunk, network drop mid-fetch, a
 *  deploy that replaced the hashed filename). Anything else is a genuine bug
 *  in the lazy-loaded component, not something this boundary should hide. */
function isChunkLoadError(error: Error): boolean {
  return error.name === 'ChunkLoadError'
    || /Loading chunk|dynamically imported module|Failed to fetch/.test(error.message);
}

/** Catches lazy-chunk load failures (flaky Wi-Fi, a deploy replacing hashed
 *  chunks mid-session) so they don't white-screen the app. Wrap this OUTSIDE
 *  <Suspense>. Every error is captured here in getDerivedStateFromError (no
 *  discrimination at that point — a static method can't see props, and a
 *  componentDidCatch-phase throw doesn't propagate to ancestor boundaries the
 *  way a render-phase throw does). Discrimination happens in render(): with
 *  rethrowNonChunkErrors, a non-chunk error is thrown from render() — a
 *  genuine render-phase throw, which React does propagate up to the nearest
 *  ancestor boundary — instead of being shown as "couldn't load this
 *  section". Without that prop (the default), every caught error renders the
 *  fallback, matching this component's original catch-all behavior. */
export class ChunkBoundary extends React.Component<ChunkBoundaryProps, ChunkBoundaryState> {
  state: ChunkBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ChunkBoundaryState {
    return { hasError: true, error };
  }

  handleRetry = () => {
    if (this.props.onRetry) {
      this.props.onRetry();
      this.setState({ hasError: false, error: null });
      return;
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const error = this.state.error;
      if (error && this.props.rethrowNonChunkErrors && !isChunkLoadError(error)) {
        throw error;
      }
      return (
        <div className="chunk-boundary-error">
          <p>Couldn't load this section.</p>
          <button type="button" onClick={this.handleRetry}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}
